import { useState, useRef, useEffect } from "react";
import "./Sidebar.css";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useSelector } from "react-redux";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import OndemandVideoRoundedIcon from "@mui/icons-material/OndemandVideoRounded";
import GradingRoundedIcon from "@mui/icons-material/GradingRounded";
import BookmarkBorderRoundedIcon from "@mui/icons-material/BookmarkBorderRounded";
import VideoCallRoundedIcon from "@mui/icons-material/VideoCallRounded";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import LibraryBooksRoundedIcon from '@mui/icons-material/LibraryBooksRounded';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import SchoolIcon from '@mui/icons-material/School';
import ExploreIcon from '@mui/icons-material/Explore';
import HistoryIcon from '@mui/icons-material/History';
import LockIcon from '@mui/icons-material/Lock';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import InboxIcon from '@mui/icons-material/Inbox';
import StorefrontIcon from '@mui/icons-material/Storefront';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import GroupsIcon from '@mui/icons-material/Groups';
import HowToVoteRoundedIcon from "@mui/icons-material/HowToVoteRounded";

let savedAccordionStates = null;
let savedScrollPosition = 0;

function Sidebar() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const voteId = searchParams.get("voteId");
  const userDetails = useSelector((state) => state.userDetails.userDetails);
  const [sidebarImgError, setSidebarImgError] = useState(false);

  useEffect(() => {
    setSidebarImgError(false);
  }, [userDetails?.photos]);

  // Get pathname segments
  const pathname = location.pathname;
  const pathnameArray = pathname.split("/");
  const pathnameArrayLastSegment = pathnameArray[pathnameArray.length - 1];
  const pathnameArrayFirstSegment = pathnameArray[1];

  // Helper function to check specific segments
  const hasSegment = (segment) => {
    return pathnameArray.includes(segment);
  };

  // Helper function to get user initials
  const getUserInitials = (firstName, lastName) => {
    const firstInitial = firstName ? firstName.charAt(0).toUpperCase() : "";
    const lastInitial = lastName ? lastName.charAt(0).toUpperCase() : "";
    return firstInitial + lastInitial;
  };

  // Helper function to generate background color based on initials
  const getBackgroundColor = (initials) => {
    const colors = [
      "#FF6B6B",
      "#4ECDC4",
      "#45B7D1",
      "#96CEB4",
      "#FFEAA7",
      "#DDA0DD",
      "#98D8C8",
      "#F7DC6F",
      "#BB8FCE",
      "#85C1E9",
      "#F8C471",
      "#82E0AA",
    ];

    let hash = 0;
    for (let i = 0; i < initials.length; i++) {
      hash = initials.charCodeAt(i) + ((hash << 5) - hash);
    }
    hash = Math.abs(hash);
    return colors[hash % colors.length];
  };

  const handleBasicDetailsClick = () => {
    navigate("/profile-details");
  };

  const handleFeedsClick = () => {
    navigate("/ballots");
  };

  const handleChangePasswordClick = () => {
    navigate("/change-password");
  };
  const handleSavedVoteClick = () => {
    navigate("/saved-ballots");
  };
  const handleVoteHistoryClick = () => {
    navigate("/ballots-history");
  };
  const handleShortsVideoClick = () => {
    navigate("/shorts-videos");
  };

  const handleMyBallotsClick = () => {
    navigate("/my-ballots");
  };

  const handleUploadShortsClick = () => {
    navigate("/upload-shorts");
  };

  const handleAddBallotClick = () => {
    navigate("/add-ballot");
  };
  const handleDonationClick = () => {
    if (location.pathname === "/amalgamate" && location.hash === "#donationPage") {
      const element = document.getElementById("donationPage");
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      navigate("/amalgamate#donationPage");
    }
  };

  const handleRewardsClick = () => {
    navigate("/rewards");
  };

  const handleHelpCenterClick = () => {
    navigate("/help-center");
  };

  const handleCertificationsClick = () => {
    navigate("/certifications");
  };

  const handleMyCertificatesClick = () => {
    navigate("/my-certificates");
  };

  const handleCommunitiesClick = () => {
    navigate("/communities");
  };

  const handleAdventureDashboardClick = () => {
    navigate("/facilitate/home");
  };

  const handleAdventureOrdersClick = () => {
    navigate("/facilitate/orders");
  };

  const handleAdventureOfferingsClick = () => {
    navigate("/facilitate/offerings");
  };

  const handleAdventureQuotesClick = () => {
    navigate("/facilitate/quotes");
  };

  const handleMarketplaceClick = () => {
    navigate("/facilitate/marketplace");
  };

  const containerRef = useRef(null);

  const isAddActive =
    hasSegment("upload-shorts") ||
    hasSegment("add-ballot") ||
    hasSegment("create-ballot");
  const isMyProfileActive =
    hasSegment("my-ballots") ||
    hasSegment("saved-ballots") ||
    hasSegment("ballots-history") ||
    hasSegment("my-certificates") ||
    hasSegment("rewards") ||
    hasSegment("profile-details") ||
    hasSegment("edit-cover-photo") ||
    hasSegment("edit-name") ||
    hasSegment("edit-dob") ||
    hasSegment("edit-address") ||
    hasSegment("edit-language") ||
    hasSegment("withdraw-request") ||
    hasSegment("government-id") ||
    hasSegment("profile-completeness") ||
    hasSegment("change-password");
  const isWalletActive =
    pathname === "/facilitate/wallet" ||
    pathname === "/facilitate/settings/payouts" ||
    pathname === "/adventure/wallet" ||
    pathname === "/adventure/settings/payouts";
  const isAdventureActive =
    (hasSegment("facilitate") || hasSegment("adventure")) && !isWalletActive;

  const [accordionStates, setAccordionStates] = useState(() => {
    if (savedAccordionStates) {
      return savedAccordionStates;
    }
    return {
      add: isAddActive,
      profile: isMyProfileActive,
      adventure: isAdventureActive,
    };
  });

  useEffect(() => {
    setAccordionStates((prev) => {
      const nextState = { ...prev };
      if (isAddActive) nextState.add = true;
      if (isMyProfileActive) nextState.profile = true;
      if (isAdventureActive) nextState.adventure = true;
      savedAccordionStates = nextState;
      return nextState;
    });
  }, [pathname, isAddActive, isMyProfileActive, isAdventureActive]);

  useEffect(() => {
    const parent = containerRef.current?.closest(".sidebarContain");
    if (parent) {
      parent.scrollTop = savedScrollPosition;

      const handleScroll = () => {
        savedScrollPosition = parent.scrollTop;
      };

      parent.addEventListener("scroll", handleScroll);
      return () => {
        parent.removeEventListener("scroll", handleScroll);
      };
    }
  }, []);

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setAccordionStates((prev) => {
      const nextState = {
        ...prev,
        [panel]: isExpanded,
      };
      savedAccordionStates = nextState;
      return nextState;
    });
  };

  return (
    <div className="Sidebar" ref={containerRef}>
      <div className="sidebarContainer">
        <div className="sidebarMenuContainer">
          {/* 1. DISCOVERY & FEEDS */}
          {/* Ballots */}
          <button className={`savedBtnContainer ${pathnameArrayFirstSegment === "ballots" ? "active" : ""}`} onClick={handleFeedsClick}>
            <GradingRoundedIcon
              sx={{
                color: pathnameArrayFirstSegment === "ballots" ? "#5AAF9A" : "white",
                fontSize: { xs: "15px", md: "25px", lg: "25px", xm: "20px" },
              }}
            />
            <span
              className={
                pathnameArrayFirstSegment === "ballots"
                  ? "selectedSidebarHeadingText"
                  : "sidebarHeadingText"
              }
            >
              Ballots
            </span>
          </button>

          {/* Shorts */}
          <button
            className={`savedBtnContainer ${pathnameArrayLastSegment === "shorts-videos" ? "active" : ""}`}
            onClick={handleShortsVideoClick}
          >
            <OndemandVideoRoundedIcon
              sx={{
                color: pathnameArrayLastSegment === "shorts-videos" ? "#5AAF9A" : "white",
                fontSize: { xs: "15px", md: "25px", lg: "25px", xm: "20px" },
              }}
            />
            <span
              className={
                pathnameArrayLastSegment === "shorts-videos"
                  ? "selectedSidebarHeadingText"
                  : "sidebarHeadingText"
              }
            >
              Shorts
            </span>
          </button>

          {/* Communities */}
          <button className={`savedBtnContainer ${hasSegment("communities") ? "active" : ""}`} onClick={handleCommunitiesClick}>
            <GroupsIcon
              sx={{
                color: hasSegment("communities") ? "#5AAF9A" : "white",
                fontSize: { xs: "15px", md: "25px", lg: "25px", xm: "20px" },
              }}
            />
            <span
              className={
                hasSegment("communities")
                  ? "selectedSidebarHeadingText"
                  : "sidebarHeadingText"
              }
            >
              Communities
            </span>
          </button>

          {/* Certifications */}
          <button className={`savedBtnContainer ${hasSegment("certifications") ? "active" : ""}`} onClick={handleCertificationsClick}>
            <WorkspacePremiumIcon
              sx={{
                color: hasSegment("certifications") ? "#5AAF9A" : "white",
                fontSize: { xs: "15px", md: "25px", lg: "25px", xm: "20px" },
              }}
            />
            <span
              className={
                hasSegment("certifications")
                  ? "selectedSidebarHeadingText"
                  : "sidebarHeadingText"
              }
            >
              Certifications
            </span>
          </button>

          {/* 2. CREATION HUB */}
          {/* Add Accordion */}
          <div className={`myProfileDropdownContainer ${isAddActive ? "active" : ""}`}>
            <Accordion
              square={false}
              expanded={!!accordionStates.add}
              onChange={handleAccordionChange("add")}
              sx={{
                backgroundColor: "rgba(255, 255, 255, 0.10)",
                color: "white",
                borderRadius: {
                  xs: "8px",
                  sm: "8px",
                  md: "8px",
                  lg: "10px",
                  xl: "12px",
                },
              }}
            >
              <AccordionSummary
                expandIcon={
                  <ExpandMoreIcon
                    sx={{
                      color: "white",
                      fontSize: {
                        xs: "15px",
                        sm: "18px",
                        md: "20px",
                        lg: "22px",
                        xl: "24px",
                      },
                    }}
                  />
                }
                aria-controls="panel2-content"
                id="panel2-header"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  minHeight: {
                    xs: "25px",
                    sm: "25px",
                    md: "35px",
                    lg: "40px",
                    xl: "48px",
                  },
                  height: {
                    xs: "25px",
                    sm: "25px",
                    md: "35px",
                    lg: "40px",
                    xl: "48px",
                  },
                  "& .MuiAccordionSummary-content": {
                    display: "flex",
                    alignItems: "center",
                    margin: 0,
                  },
                }}
              >
                <div className="myProfileHeading">
                  <VideoCallRoundedIcon
                    sx={{
                      color: isAddActive ? "#5AAF9A" : "white",
                      fontSize: {
                        xs: "15px",
                        md: "25px",
                        lg: "25px",
                        xm: "20px",
                      },
                    }}
                  />
                  <span className={isAddActive ? "selectedSidebarHeadingText" : "sidebarHeadingText"}>Add</span>
                </div>
              </AccordionSummary>
              <AccordionDetails>
                <div className="dropdownBtnsContainer">
                  <button
                    className={
                      pathnameArrayLastSegment === "add-ballot" ||
                      pathnameArrayLastSegment === "create-ballot"
                        ? "selectedDropdownBtn"
                        : "dropdownBtn"
                    }
                    onClick={handleAddBallotClick}
                  >
                    <HowToVoteRoundedIcon />
                    Add Ballot
                  </button>
                  <button
                    className={
                      pathnameArrayLastSegment === "upload-shorts"
                        ? "selectedDropdownBtn"
                        : "dropdownBtn"
                    }
                    onClick={handleUploadShortsClick}
                  >
                    <VideoCallRoundedIcon />
                    Upload Shorts
                  </button>
                </div>
              </AccordionDetails>
            </Accordion>
          </div>

          {/* 3. COMMERCE & FACILITATION */}
          {/* Facilitate Accordion */}
          <div className={`myProfileDropdownContainer ${isAdventureActive ? "active" : ""}`}>
            <Accordion
              square={false}
              expanded={!!accordionStates.adventure}
              onChange={handleAccordionChange("adventure")}
              sx={{
                backgroundColor: "rgba(255, 255, 255, 0.10)",
                color: "white",
                borderRadius: {
                  xs: "8px",
                  sm: "8px",
                  md: "8px",
                  lg: "10px",
                  xl: "12px",
                },
              }}
            >
              <AccordionSummary
                expandIcon={
                  <ExpandMoreIcon
                    sx={{
                      color: "white",
                      fontSize: {
                        xs: "15px",
                        sm: "18px",
                        md: "20px",
                        lg: "22px",
                        xl: "24px",
                      },
                    }}
                  />
                }
                aria-controls="panel-adventure-content"
                id="panel-adventure-header"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  minHeight: {
                    xs: "25px",
                    sm: "25px",
                    md: "35px",
                    lg: "40px",
                    xl: "48px",
                  },
                  height: {
                    xs: "25px",
                    sm: "25px",
                    md: "35px",
                    lg: "40px",
                    xl: "48px",
                  },
                  "& .MuiAccordionSummary-content": {
                    display: "flex",
                    alignItems: "center",
                    margin: 0,
                  },
                }}
              >
                <div className="myProfileHeading">
                  <ExploreIcon
                    sx={{
                      color: isAdventureActive ? "#5AAF9A" : "white",
                      fontSize: {
                        xs: "15px",
                        md: "25px",
                        lg: "25px",
                        xm: "20px",
                      },
                    }}
                  />
                  <span
                    className={
                      isAdventureActive
                        ? "selectedSidebarHeadingText"
                        : "sidebarHeadingText"
                    }
                  >
                    Facilitate
                  </span>
                </div>
              </AccordionSummary>
              <AccordionDetails>
                <div className="dropdownBtnsContainer">
                  <button
                    className={
                      hasSegment("marketplace") || (pathnameArray[2] === "offerings" && pathnameArray[3] && pathnameArray[3] !== "create" && pathnameArray[4] !== "edit")
                        ? "selectedDropdownBtn"
                        : "dropdownBtn"
                    }
                    onClick={handleMarketplaceClick}
                  >
                    <StorefrontIcon />
                    Amalgamate Marketplace
                  </button>
                  <button
                    className={
                      pathname === "/facilitate/home" || pathname === "/adventure/home"
                        ? "selectedDropdownBtn"
                        : "dropdownBtn"
                    }
                    onClick={handleAdventureDashboardClick}
                  >
                    <DashboardIcon />
                    Dashboard
                  </button>
                  <button
                    className={
                      pathnameArray[2] === "offerings" && (!pathnameArray[3] || pathnameArray[3] === "create" || pathnameArray[4] === "edit")
                        ? "selectedDropdownBtn"
                        : "dropdownBtn"
                    }
                    onClick={handleAdventureOfferingsClick}
                  >
                    <StorefrontIcon />
                    Your Offerings
                  </button>
                  <button
                    className={
                      hasSegment("orders")
                        ? "selectedDropdownBtn"
                        : "dropdownBtn"
                    }
                    onClick={handleAdventureOrdersClick}
                  >
                    <ReceiptLongIcon />
                    Orders
                  </button>
                  <button
                    className={
                      hasSegment("quotes")
                        ? "selectedDropdownBtn"
                        : "dropdownBtn"
                    }
                    onClick={handleAdventureQuotesClick}
                  >
                    <ReceiptLongIcon />
                    Custom Quotes
                  </button>
                  <button
                    className={
                      hasSegment("fulfillment")
                        ? "selectedDropdownBtn"
                        : "dropdownBtn"
                    }
                    onClick={() => navigate("/facilitate/fulfillment")}
                  >
                    <InboxIcon />
                    Fulfillment Orders
                  </button>
                  <button
                    className={
                      pathname === "/facilitate/reviews" || pathname === "/adventure/reviews"
                        ? "selectedDropdownBtn"
                        : "dropdownBtn"
                    }
                    onClick={() => navigate("/facilitate/reviews")}
                  >
                    <GradingRoundedIcon />
                    Your Reviews
                  </button>
                </div>
              </AccordionDetails>
            </Accordion>
          </div>

          {/* Earnings & Wallet */}
          <button
            className={`savedBtnContainer ${isWalletActive ? "active" : ""}`}
            onClick={() => navigate("/facilitate/wallet")}
          >
            <AccountBalanceWalletIcon
              sx={{
                color: isWalletActive ? "#5AAF9A" : "white",
                fontSize: { xs: "15px", md: "25px", lg: "25px", xm: "20px" },
              }}
            />
            <span
              className={
                isWalletActive
                  ? "selectedSidebarHeadingText"
                  : "sidebarHeadingText"
              }
            >
              Earnings &amp; Wallet
            </span>
          </button>

          {/* 4. PERSONAL HUB (MY PROFILE) */}
          <div className={`myProfileDropdownContainer ${isMyProfileActive ? "active" : ""}`}>
            <Accordion
              square={false}
              expanded={!!accordionStates.profile}
              onChange={handleAccordionChange("profile")}
              sx={{
                backgroundColor: "rgba(255, 255, 255, 0.10)",
                color: "white",
                borderRadius: {
                  xs: "8px",
                  sm: "8px",
                  md: "8px",
                  lg: "10px",
                  xl: "12px",
                },
              }}
            >
              <AccordionSummary
                expandIcon={
                  <ExpandMoreIcon
                    sx={{
                      color: "white",
                      fontSize: {
                        xs: "15px",
                        sm: "18px",
                        md: "20px",
                        lg: "22px",
                        xl: "24px",
                      },
                    }}
                  />
                }
                aria-controls="panel2-content"
                id="panel2-header"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  minHeight: {
                    xs: "25px",
                    sm: "25px",
                    md: "35px",
                    lg: "40px",
                    xl: "48px",
                  },
                  height: {
                    xs: "25px",
                    sm: "25px",
                    md: "35px",
                    lg: "40px",
                    xl: "48px",
                  },
                  "& .MuiAccordionSummary-content": {
                    display: "flex",
                    alignItems: "center",
                    margin: 0,
                  },
                }}
              >
                <div className="myProfileHeading">
                  <PersonOutlineRoundedIcon
                    sx={{
                      color: isMyProfileActive ? "#5AAF9A" : "white",
                      fontSize: {
                        xs: "15px",
                        md: "25px",
                        lg: "25px",
                        xm: "20px",
                      },
                    }}
                  />
                  <span className={isMyProfileActive ? "selectedSidebarHeadingText" : "sidebarHeadingText"}>My Profile</span>
                </div>
              </AccordionSummary>
              <AccordionDetails>
                <div className="dropdownBtnsContainer">
                  <button
                    className={
                      hasSegment("profile-details") ||
                      hasSegment("profile-completeness") ||
                      hasSegment("edit-cover-photo") ||
                      hasSegment("edit-name") ||
                      hasSegment("edit-dob") ||
                      hasSegment("edit-address") ||
                      hasSegment("edit-language") ||
                      hasSegment("withdraw-request") ||
                      hasSegment("government-id")
                        ? "selectedDropdownBtn"
                        : "dropdownBtn"
                    }
                    onClick={handleBasicDetailsClick}
                  >
                    <PersonOutlineRoundedIcon />
                    Profile Details
                  </button>
                  <button
                    className={
                      pathnameArrayFirstSegment === "my-ballots"
                        ? "selectedDropdownBtn"
                        : "dropdownBtn"
                    }
                    onClick={handleMyBallotsClick}
                  >
                    <LibraryBooksRoundedIcon />
                    My Ballots
                  </button>
                  <button
                    className={
                      pathnameArrayLastSegment === "ballots-history"
                        ? "selectedDropdownBtn"
                        : "dropdownBtn"
                    }
                    onClick={handleVoteHistoryClick}
                  >
                    <HistoryIcon />
                    My Ballots History
                  </button>
                  <button
                    className={
                      hasSegment("saved-ballots")
                        ? "selectedDropdownBtn"
                        : "dropdownBtn"
                    }
                    onClick={handleSavedVoteClick}
                  >
                    <BookmarkBorderRoundedIcon />
                    Saved Ballots
                  </button>
                  <button
                    className={
                      hasSegment("my-certificates")
                        ? "selectedDropdownBtn"
                        : "dropdownBtn"
                    }
                    onClick={handleMyCertificatesClick}
                  >
                    <SchoolIcon />
                    My Certificates
                  </button>
                  {userDetails?.isRewardEnable && (
                    <button
                      className={
                        pathnameArrayLastSegment === "rewards"
                          ? "selectedDropdownBtn"
                          : "dropdownBtn"
                      }
                      onClick={handleRewardsClick}
                    >
                      <WorkspacePremiumIcon />
                      Rewards
                    </button>
                  )}
                  <button
                    className={
                      pathnameArrayLastSegment === "change-password"
                        ? "selectedDropdownBtn"
                        : "dropdownBtn"
                    }
                    onClick={handleChangePasswordClick}
                  >
                    <LockIcon />
                    Change Password
                  </button>
                </div>
              </AccordionDetails>
            </Accordion>
          </div>

          {/* 5. CIVIC IMPACT & SUPPORT */}
          {/* Donate Now */}
          <button className={`savedBtnContainer ${hasSegment("amalgamate") ? "active" : ""}`} onClick={handleDonationClick}>
            <VolunteerActivismIcon
              sx={{
                color: hasSegment("amalgamate") ? "#5AAF9A" : "white",
                fontSize: { xs: "15px", md: "25px", lg: "25px", xm: "20px" },
              }}
            />
            <span className={hasSegment("amalgamate") ? "selectedSidebarHeadingText" : "sidebarHeadingText"}>Donate Now</span>
          </button>

          {/* Help Center */}
          <button className={`savedBtnContainer ${hasSegment("help-center") ? "active" : ""}`} onClick={handleHelpCenterClick}>
            <HelpOutlineRoundedIcon
              sx={{
                color: hasSegment("help-center") ? "#5AAF9A" : "white",
                fontSize: { xs: "15px", md: "25px", lg: "25px", xm: "20px" },
              }}
            />
            <span
              className={
                hasSegment("help-center")
                  ? "selectedSidebarHeadingText"
                  : "sidebarHeadingText"
              }
            >
              Help Center
            </span>
          </button>
        </div>
        <div className="userProfileDetails">
          <div className="profileImageContainer">
            {userDetails?.photos && !sidebarImgError ? (
              <img
                src={userDetails.photos}
                alt="Profile"
                className="profileImage"
                onError={() => setSidebarImgError(true)}
              />
            ) : (
              <div
                className="profileImage"
                style={{
                  backgroundColor: getBackgroundColor(
                    getUserInitials(
                      userDetails?.firstName,
                      userDetails?.lastName
                    )
                  ),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "white",
                  fontWeight: "bold",
                  fontSize: "14px",
                  fontFamily: "Arial, sans-serif",
                }}
              >
                {getUserInitials(userDetails?.firstName, userDetails?.lastName)}
              </div>
            )}
          </div>
          <div className="userNameContainer">
            <span className="userName">
              {userDetails?.firstName} {userDetails?.lastName}
            </span>
            <span className="userEmail">{userDetails?.email}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Sidebar;
